import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { useForm } from 'react-hook-form'; // <-- Import `useForm` here
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from "react-icons/md"; // <-- NEW: Import for the close icon
import { BsBank } from "react-icons/bs";
import { HiOutlineReceiptTax } from "react-icons/hi";
import { FaRegBuilding } from "react-icons/fa";
import { FaUserCircle } from "react-icons/fa";
import { MdAccountCircle } from "react-icons/md";
import {Layout, EmployeeProfile, EmployeeConfig} from '../../../components';
import {handleApi} from '../../../utils/utils';
import AppContext from '../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// import '../styles/style.css';

const NewEmployee = () => {
  const { reset, control, register, handleSubmit, formState: { errors } } = useForm();
  const [employee, setEmployee] = useState({
    date_of_birth: '',
    salutation: '',
    first_name: '',
    last_name: '',
    gender: '',
    phone_number: '',
    email: '',
    job_title: '',
    department_id: '',
    hire_date: new Date().toISOString().split('T')[0], // Initialize with today's date in YYYY-MM-DD format
  });
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayImg, setDisplayImg] = useState(''); 
  const [imgType, setImgType] = useState('');
  const { userData, companyProfile, bank, currentColor, actionCount, setActionCount } = useContext(AppContext);

  //fetch departments when the component mounts
  useEffect(() => {fetchDepartments();}, []); 
  
  // Reset the form when `employee` state changes (initial or after submit)
  useEffect(() => {reset(employee); }, [employee, reset]);
  
  const fetchDepartments = async () => {
    const apiEndpoint = 'departments';
    const apiMethod = 'GET';
    const parameters = {};
    setIsLoading(true);
    const params = {};
    try {
      const response = await handleApi(apiEndpoint, params, userData, apiMethod, parameters);
      if (response.error) {
        console.error('Error fetching departments', response.errorMessage);
        return; // Exit function to avoid further processing
      }
      const { status, statusText } = response;
      if (status === 200) {
        const fetchedDepartments = response.data; // fetched departments
        //format departments for select options
        const transformedDepartments = fetchedDepartments.map(department => ({
          value: department.id,
          label: department.name
        }));
    
        // Now update the state with the transformed data
        setDepartments(transformedDepartments);
      } else {
        console.error(`Error ${status}: ${statusText}`); // Handle non-200 responses
      }
    } catch (error) {
      console.error(error.message || 'An unexpected error occurred.'); // Handle unexpected errors
    } finally {setIsLoading(false);}
  };
  
  // Handle the cancel action
  const handleCancel = () => {
    // Create an object with default values for each field from EmployeeConfig
    const resetValues = EmployeeConfig.reduce((acc, field) => {
      acc[field.name] = field.defaultValue === undefined ? '' : field.defaultValue; // Handle undefined defaultValue
      // Special handling for date fields if defaultValue is not suitable for empty
      if (field.type === 'date') {
        acc[field.name] = ''; // Or null, depending on how you want to represent empty date
      }
      return acc;
    }, {});
    // For fields like salutation, first_name, last_name not in EmployeeConfig, explicitly set them
    resetValues.salutation = '';
    resetValues.first_name = '';
    resetValues.last_name = '';
    // Reset the form state with default values
    reset(resetValues); // This will clear the react-hook-form state
    // Optionally reset employee state as well, to ensure consistency and trigger useEffect if needed
    setEmployee({
      date_of_birth: '',
      salutation: '',
      first_name: '',
      last_name: '',
      gender: '', // Set to empty or your default for a fresh form
      phone_number: '',
      email: '',
      job_title: '',
      department_id: '',
      hire_date: '', // Clear the hire date too
    });

    // Reset the image and other states if needed
    setDisplayImg('');
    setImgType('');
  };

  const navigate = useNavigate(); 

  // handle closing the page
  const handleClosePage = () => {
   navigate(-1);   // Navigate back to the previous page
  };

  const convertFile = (files) => {
    if (files && files.length > 0 && files[0] instanceof File) {
      const fileRef = files[0];
      const fileType = fileRef.type;
      const reader = new FileReader();
      reader.readAsBinaryString(fileRef);
      reader.onload = (ev) => {
        const base64Data = btoa(ev.target.result); // Get base64 string
        // Update the states that hold the image data
        setDisplayImg(base64Data);
        setImgType(fileType);
        setEmployee(prevEmployee => ({
          ...prevEmployee,
          photo: { data: base64Data, contentType: fileType } // Direct photo property
        }));
      };
    }
  };

  const handleImgChange = (event) => {
    const { name, files } = event.target;
    if (name === "logo") {convertFile(files);}
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
  };
  
  const handleEmployeeProfileFormSubmit = async (dataFromForm) => {
    // START SAFE DEBUGGING CODE
    try {
      console.log('Form data received from react-hook-form (safe stringify):', JSON.stringify(dataFromForm, null, 2));
    } catch (e) {
      console.error("Failed to stringify `dataFromForm`. Circular reference found:", dataFromForm);
      toast.error("Form data is corrupted. Check the console for details.");
      setIsLoading(false); // Make sure to stop loading if an error occurs
      return; // Stop processing
    }
    // Destructure out any fields you don't want to send directly
    const {
      create_uid,
      create_date,
      update_date,
      photo_url, 
      ...payload
    } = dataFromForm;

    // Add the image data from state to your payload
    if (displayImg && imgType) {
      payload.photo = {
        data: displayImg,
        contentType: imgType
      };
    } else {payload.photo = null; }

    try {
      console.log('Final payload to send to API (safe stringify):', JSON.stringify(payload, null, 2));
    } catch (e) {
      console.error("Final payload has a circular reference. Inspect the object below.", payload);
      toast.error("The final payload is corrupted. Check the console for details.");
      setIsLoading(false);
      return;
    }

    const apiMethod = payload.id ? 'PUT' : 'POST';
    const parameters = { ...(payload.id && { id: payload.id }) };
    setIsLoading(true);
    try {
      const response = await handleApi('employees', payload, userData, apiMethod, parameters);
      if (response.status === 200 || response.status === 201) {
        toast.success('Employee profile saved successfully!');
        handleCancel();
      } else {
        toast.error(`Error saving employee: ${response.statusText || 'An unknown error occurred'}`);
      }
    } catch (error) {
      toast.error('Error saving employee: ' + error.message);
    } finally {
      setActionCount(actionCount + 1);
      setIsLoading(false);
    }
  };
  // Generate a base64-encoded SVG user icon for the default photo
  // The SVG path is from a common user icon, similar to FaUserCircle
  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 512 512" fill="${currentColor}"><path d="M256,0C114.6,0,0,114.6,0,256s114.6,256,256,256s256-114.6,256-256S397.4,0,256,0z M256,79.5c37.5,0,67.9,30.4,67.9,67.9S293.5,215.4,256,215.4S188.1,185,188.1,147.4S218.5,79.5,256,79.5z M256,441.6c-70.5,0-134.4-29.3-177.3-76.3c-0.3-0.3-0.5-0.6-0.8-1c-0-0-0-0-0-0.1C72,322,112.5,274.6,161.4,274.6h189.1c48.9,0,89.4,47.4,83.6,90.5c-0,0-0,0-0.1,0.1c-0.3,0.3-0.5,0.7-0.8,1C390.4,412.3,326.5,441.6,256,441.6z" /></svg>`;
  const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
  const defaultPhotoUrl = `data:image/svg+xml;base64,${base64Svg}`;

  // Conditionally set the photo URL to either the uploaded image or the default SVG
  const photoUrl = displayImg && imgType ? `data:${imgType};base64,${displayImg}` : defaultPhotoUrl;


  return (
    <Layout>
      <div className="m-1 sm:m-2">
        {/* New flex container for the title and close button */}
        <div className="relative flex justify-between items-center">
          <h1 className="text-center font-bold flex-grow">NEW EMPLOYEE</h1>
          <button
            onClick={handleClosePage}
            className="p-1 rounded-lg text-red-400 bg-white hover:bg-gray-50 hover:text-gray-600"
          >
            <MdOutlineCancel color={currentColor} size={30} />
          </button>
        </div>
        {/* Tab Buttons */}
        
        <ToastContainer position="top-center" autoClose={2000} />

        {isLoading ? (
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Loading. Please wait...</p>
          </div>
        ) : (
          <div className="bg-white p-2 sm:p-4 my-4 my-auto mx-auto max-w-[80%] rounded-xl shadow-2xl">
            <div className="mx-auto border-b border-gray-400 bg-gray-500 transition-all duration-200 cursor-pointer rounded-xl shadow-2xl p-8">
              <EmployeeProfile
                  fields={EmployeeConfig}
                  control={control}
                  register={register}
                  photo={photoUrl} // Pass the state variable that holds the image data
                  onChange={handleChange}
                  departments={departments} // Pass departments to EmployeeProfile
                  handleImgChange={handleImgChange}
                  onSubmit={handleSubmit(handleEmployeeProfileFormSubmit)}
                  employeeData={employee} // Ensure employeeData is passed correctly for initial values
                  onCancel={handleCancel} // Pass the proper clearing function here
                  errors={errors} // <-- Pass `errors` down
                  reset={reset} // <-- NEW: Pass the `reset` function from `useForm`
                  isLoading={isLoading}
                />
              </div>
            
          </div>
        )}
      </div>
    </Layout>
  );
};
export default NewEmployee;